<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { getPollTemplates } from '@/services/pollTemplateService.js'

import Loading from '@/components/Generics/Loadings/Loading.vue'
import Paginazione from '@/components/Generics/Paginations/Paginazione.vue'

import { usePageNumberStore } from '@/services/store.js'
import { client } from '@/services/fetch.js'
import { toast } from '@/services/toast.js'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faChartLine,
  faClipboardList,
  faXmark,
  faCheck,
  faTrash,
  faPencil,
  faMagnifyingGlass,
  faRotate,
  faGears,
  faPlus
} from '@fortawesome/free-solid-svg-icons'

library.add(
  faChartLine,
  faClipboardList,
  faXmark,
  faTrash,
  faCheck,
  faPencil,
  faMagnifyingGlass,
  faRotate,
  faGears,
  faPlus
)

const route = useRoute()

const pageNumberStore = usePageNumberStore()
const polls = ref([])
const tags = ref([])
const canEdit = ref(false)
const canDelete = ref(false)
const canView = ref(false)
const collapseList = ref({})
const loading = ref(true)
const currentPage = ref(1)

const modal = ref({
  enabled: false,
  title: '',
  action: '',
  args: null
})

const modalActivate = ref({
  enabled: false,
  poll: null,
  loading: false
})

const pagination = ref({
  nPages: 1
})

const formatTags = computed(() => (pollTags) => {
  let tags = ''
  for (let i = 0; i < pollTags.length; i++) {
    tags += pollTags[i]
    if (i < 3 && i < pollTags.length - 1) {
      tags += ', '
    }
    if (i === 3) {
      break
    }
  }
  return tags
})

onMounted(async () => {
  let page = pageNumberStore.getPage(route.name) ?? 1
  canEdit.value = route.meta.permissions.canEdit ?? false
  canDelete.value = route.meta.permissions.canDelete ?? false
  canView.value = route.meta.permissions.canView ?? false

  if (!canView.value) {
    return
  }

  await getPolls(page)
})

watch(
  () => polls,
  async () => {
    await nextTick()
    const collapseElementList = Array.from(document.querySelectorAll('.collapse'))
    collapseElementList.forEach((el) => {
      collapseList[el.id] = new bootstrap.Collapse(el, { toggle: false })
    })
  },
  { immediate: true }
)

const reloadPolls = async () => {
  polls.value = []
  await getPolls()
}

const getPolls = async (page = 1) => {
  loading.value = true
  try {
    const response = await getPollTemplates(page)
    if (response.pagination) {
      calculatePagination(response.pagination)
    }
    polls.value = response.data
    loading.value = false
  } catch (error) {
    toast.create('Errore nel caricamento dei dati, riprova più tardi', 'error')
    loading.value = false
  }
}

const calculatePagination = (pageData) => {
  pagination.value = pageData
  pagination.value.nPages = Math.ceil(pageData['total_items'] / pageData['items_per_page'])
}

const goToPage = async page => {
  await getPolls(page)
  currentPage.value = page
}

const confirmModal = async (title, action, args) => {
  if (!modal.value.enabled) {
    modal.value.action = action
    modal.value.args = args
    modal.value.title = title
    modal.value.enabled = true
  } else {
    await deletePoll(modal.value.args)
    resetModal()
  }
}

const confirmActivatePollTemplate = async () => {
  let poll = modalActivate.value.poll
  modalActivate.value.loading = true

  let pollTemplateActive = {
    title: poll['title'],
    description: poll['description'],
    tags: poll['tags'],
    state: 'active'
  }

  try {
    let url = `/config/templates/polls/api/poll-template/${poll['id']}`
    await client.put(url, pollTemplateActive)
    toast.create('Template attivato con successo', 'success')
  } catch (error) {
    toast.create('Errore durante attivazione template, riprova', 'error')
  }

  modalActivate.value.enabled = false
  modalActivate.value.poll = null
  modalActivate.value.loading = false
  await getPolls(currentPage.value)
}

const resetModal = () => {
  Object.assign(modal.value, { enabled: false, action: '', title: '', args: null })
}

async function deletePoll(poll) {
  try {
    loading.value = true
    let url = `/config/templates/polls/api/poll-template/${poll.id}`
    await client.delete(url)
    await reloadPolls()
    loading.value = false
    toast.create('Template eliminato con successo', 'success')
  } catch (error) {
    loading.value = false
    toast.create('Errore durante l\'eliminazione, riprova', 'error')
  }
}

const getQuestionGroupByReference = (reference, questionGroups) => {
  let ret = null
  questionGroups.forEach((questionGroup) => {
    if (questionGroup.reference === reference) {
      ret = questionGroup
    } else {
      questionGroup.questions.forEach((question) => {
        ret = getQuestionGroupByReference(reference, question['childQuestionGroups'])
      })
    }
  })
  return ret
}
</script>

<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 px-0">
        <div class="card shadow-none">
          <div class="card-body px-0">

            <loading v-if="loading"></loading>

            <div v-if="!loading" class="table-responsive">
              <table class="table table-striped m-0">
                <thead>
                <tr>
                  <th>Nome</th>
                  <th>N° revisione</th>
                  <th>Tags</th>
                  <th>Azioni</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(poll, index) in polls" :key="index" v-if="polls.length">
                  <td>
                    {{ poll['title'] }}
                  </td>
                  <td>
                    {{ poll['revisionNumber'] }}
                  </td>
                  <td>
                    {{ formatTags(poll['tags']) }}
                  </td>
                  <td>
                    <div class="d-flex justify-content-end">
                      <router-link v-if="canEdit && poll['state'] !== 'draft'"
                                   :to="{name: 'admin/PollTemplateStats', params: {id: poll.id}}">
                        <button type="button"
                                class="btn btn-primary btn-sm ms-2"
                                title="Mostra statistiche">
                          <font-awesome-icon icon="fa-solid fa-chart-line" />
                        </button>
                      </router-link>
                      <router-link v-if="canEdit && poll['state'] !== 'draft'"
                                   :to="{name: 'admin/PollTemplateCampaign', params: {id: poll.id}}">
                        <button title="Visualizza campagne"
                                type="button"
                                class="btn btn-primary btn-sm ms-2">
                          <font-awesome-icon icon="fa-solid fa-clipboard-list" />
                        </button>
                      </router-link>
                      <router-link v-if="canEdit"
                                   :to="{name: 'admin/PollTemplate--edit' , params: {id: poll.id}}">
                        <button class="btn btn-sm btn-primary ms-2" title="Modifica template">
                          <font-awesome-icon icon="fa-solid fa-pencil" />
                        </button>
                      </router-link>
                      <button
                        v-if="poll.state === 'draft' && canEdit"
                        class="btn btn-sm btn-primary ms-2" title="Attiva questionario"
                        @click="()=>{modalActivate.enabled = true; modalActivate.poll = poll}"
                      >
                        <font-awesome-icon icon="fa-solid fa-check" />
                      </button>
                      <router-link v-if="poll['enabledActions']['update'] && canEdit"
                                   :to="{name: 'admin/PollTemplate--detail' , params: {id: poll.id}}">
                        <button class="btn btn-primary btn-sm ms-2"
                                title="Proprietà template">
                          <font-awesome-icon icon="fa-solid fa-gears" />
                        </button>
                      </router-link>
                      <button v-if="canDelete && poll['enabledActions']['delete']"
                              @click="confirmModal('Elimina questionario', 'Eliminare questionario <b>'+poll.title+'</b>?', poll)"
                              class="btn btn-sm btn-danger ms-2"
                              title="Elimina questionario">
                        <font-awesome-icon icon="fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-else>
                  <td colspan="4" class="text-center">Non ci sono questionari</td>
                </tr>
                </tbody>
              </table>
            </div>

            <div v-if="!loading && pagination.nPages > 1" class="d-flex justify-content-end m-2 mt-4">
              <Paginazione
                :total-pages="pagination.nPages"
                @page-changed="goToPage"
              />
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>

  <div :class="'modal '+ (modal.enabled ? 'd-block' :'')" id="exampleModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h1 class="modal-title fs-5" id="exampleModalLabel">{{ modal.title }}</h1>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"
                  @click="resetModal"></button>
        </div>
        <div class="modal-body" v-html="modal.action">
        </div>
        <div class="modal-footer border-top-0">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal" @click="resetModal">Annulla
          </button>
          <button type="button" class="btn btn-danger" data-bs-dismiss="modal"
                  @click="confirmModal"
                  :disabled="loading">Elimina
          </button>
        </div>
      </div>
    </div>
  </div>


  <transition name="modal">
    <div v-if="modalActivate.enabled" :class="'modal fade show d-block'" id="activatePollModal" tabindex="-1"
         aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h1 class="modal-title fs-5" id="activatePollModalLabel">Attivazione questionario</h1>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"
                    :disabled="modalActivate.loading"
                    @click="()=>{modalActivate.enabled = false; modalActivate.poll = null}"></button>
          </div>
          <div class="modal-body">
            Sicuro di voler attivare il questionario?
          </div>
          <div class="modal-footer border-top-0">
            <button
              type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal"
              :disabled="modalActivate.loading"
              @click="()=>{modalActivate.enabled = false; modalActivate.poll = null}"
            >
              Annulla
            </button>
            <button
              type="button" class="btn btn-primary" data-bs-dismiss="modal"
              @click="confirmActivatePollTemplate"
              :disabled="modalActivate.loading"
            >
              Attiva
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style lang="scss">
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}
</style>


